'use client';

import { useEffect } from 'react';
import { supabase } from '@/shared/infrastructure/supabase/client';

type UseOrderPaymentWatcherArgs = {
  orderId: string | null;
  /** Solo se suscribe mientras el checkout espera el pago (QR o terminal). */
  enabled: boolean;
  onApproved: (orderId: string) => void;
  onRejected: (orderId: string) => void;
  /** La espera superó `timeoutMs`: hay que ofrecer una salida al cajero. */
  onTimeout?: (orderId: string) => void;
  timeoutMs?: number;
};

type PaymentStatus = { status?: string | null };

const POLL_INTERVAL_MS = 5_000;

/** Espera máxima del pago antes de ofrecer una salida (5 minutos). */
export const DEFAULT_PAYMENT_TIMEOUT_MS = 5 * 60 * 1_000;

/**
 * Observa el estado del pago de una orden.
 *
 * Consulta la fila de `orders` (no solo `payment_id`), así sigue funcionando cuando la orden todavía
 * no tiene pago asociado — antes cortaba en silencio y el modal quedaba esperando para siempre.
 * Usa Realtime sobre `order_payments` cuando hay `payment_id` y polling de respaldo cada 5 s.
 *
 * No toca UI ni navegación: solo notifica el estado con callbacks.
 */
export function useOrderPaymentWatcher({
  orderId,
  enabled,
  onApproved,
  onRejected,
  onTimeout,
  timeoutMs = DEFAULT_PAYMENT_TIMEOUT_MS,
}: UseOrderPaymentWatcherArgs) {
  useEffect(() => {
    if (!enabled || !orderId) return;

    let isCancelled = false;
    let settled = false;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let subscribedPaymentId: string | null = null;

    const settle = (status: string | null | undefined) => {
      if (isCancelled || settled) return;
      if (status === 'completed') {
        settled = true;
        onApproved(orderId);
        return;
      }
      if (status === 'canceled' || status === 'error') {
        settled = true;
        onRejected(orderId);
      }
    };

    const subscribeToPayment = (paymentId: string) => {
      if (subscribedPaymentId === paymentId || isCancelled) return;
      if (channel) supabase.removeChannel(channel);

      subscribedPaymentId = paymentId;
      channel = supabase
        .channel(`order-payment:${paymentId}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'order_payments', filter: `id=eq.${paymentId}` },
          payload => {
            settle((payload.new as PaymentStatus)?.status);
          }
        )
        .subscribe();
    };

    const check = async () => {
      if (isCancelled || settled) return;

      const { data: order } = await supabase
        .from('orders')
        .select('payment_id, order_payments(status)')
        .eq('id', orderId)
        .single();

      if (isCancelled || !order) return;

      const paymentId = order.payment_id ?? null;
      if (paymentId) subscribeToPayment(paymentId);

      // Supabase devuelve un array en las relaciones 1-to-many.
      const rawPayments: unknown = order.order_payments;
      const payments = (Array.isArray(rawPayments) ? rawPayments : [rawPayments]) as (PaymentStatus | null)[];

      if (payments.some(payment => payment?.status === 'completed')) {
        settle('completed');
        return;
      }

      const rejected = payments.find(payment => payment?.status === 'canceled' || payment?.status === 'error');
      if (rejected) settle(rejected.status);
    };

    void check();
    pollInterval = setInterval(() => {
      void check();
    }, POLL_INTERVAL_MS);

    const timeout = setTimeout(() => {
      if (isCancelled || settled) return;
      settled = true;
      onTimeout?.(orderId);
    }, timeoutMs);

    return () => {
      isCancelled = true;
      clearInterval(pollInterval);
      clearTimeout(timeout);
      if (channel) supabase.removeChannel(channel);
    };
  }, [enabled, orderId, onApproved, onRejected, onTimeout, timeoutMs]);
}
